import { Phone, Mail, ArrowUpRight } from "lucide-react";
import { useReveal } from "../../hooks/useReveal";

export const Hero = () => {
  const ref = useReveal();

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden"
    >
      <div className="mx-auto max-w-[1320px] px-6 md:px-10 lg:px-16 pt-10 md:pt-12 pb-20 md:pb-20 lg:pb-24">
        <div
          ref={ref}
          className="reveal grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center"
        >
          {/* Left text column */}
          <div className="lg:col-span-7">
            {/* Status badge */}
            <div className="inline-flex items-center gap-2 mb-8 px-3 py-1.5 border border-[#1B2E26]/25 bg-[#FDFBF7]">
              <span
                aria-hidden="true"
                className="w-1.5 h-1.5 rounded-full bg-[#1B2E26]"
              />
              <span className="text-[10px] uppercase tracking-[0.22em] text-[#1B2E26]">
                Accepting a few new patients
              </span>
            </div>

            <p
              data-testid="hero-overline"
              className="text-[11px] uppercase tracking-[0.28em] text-[#4A4D4A] mb-8"
            >
              Clinical Psychologist · Nottingham &amp; Online UK
            </p>

            <h1
              data-testid="hero-headline"
              className="font-serif-display text-[2.25rem] sm:text-[2.75rem] md:text-5xl lg:text-[3.75rem] xl:text-[4.5rem] leading-[1.05] tracking-[-0.02em] text-[#1A1C1A] font-medium"
            >
              Managing on the outside.{" "}
              <em className="italic font-normal text-[#1B2E26]">
                Still stuck on the inside.
              </em>
            </h1>

            <p
              data-testid="hero-subheadline"
              className="mt-7 max-w-[34rem] text-base md:text-lg leading-[1.7] text-[#4A4D4A] font-sans-body font-light"
            >
              For people who want the symptoms to ease &mdash; and the patterns
              underneath them to actually change.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <a
                href="tel:07501211348"
                data-testid="hero-book-call-button"
                className="group inline-flex items-center justify-center gap-3 bg-[#1B2E26] text-[#FDFBF7] hover:bg-[#2C453B] px-7 py-4 text-[11px] uppercase tracking-[0.22em] transition-colors font-serif-display font-medium"
              >
                <Phone size={14} strokeWidth={1.6} />
                Book a free 15-min call
                <ArrowUpRight
                  size={15}
                  strokeWidth={1.6}
                  className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </a>
              <a
                href="mailto:ghoshchandril@gmail.com"
                data-testid="hero-email-button"
                className="inline-flex items-center justify-center gap-3 border border-[#1B2E26] text-[#1B2E26] hover:bg-[#1B2E26] hover:text-[#FDFBF7] px-7 py-4 text-[11px] uppercase tracking-[0.22em] transition-colors font-serif-display font-medium"
              >
                <Mail size={14} strokeWidth={1.6} />
                Email enquiry
              </a>
            </div>

            <div
              data-testid="hero-credentials-strip"
              className="mt-14 grid grid-cols-2 gap-8 max-w-lg border-t border-[#DCD7CE] pt-7"
            >
              <div>
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#4A4D4A] mb-1.5">
                  HCPC Registered
                </p>
                <p className="font-serif-display text-base md:text-lg text-[#1A1C1A] font-medium leading-snug">
                  Clinical Psychologist
                </p>
                <p className="text-xs text-[#4A4D4A] mt-0.5">PYL045683</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#4A4D4A] mb-1.5">
                  BPS Chartered
                </p>
                <p className="font-serif-display text-base md:text-lg text-[#1A1C1A] font-medium leading-snug">
                  Member 546878
                </p>
                <p className="text-xs text-[#4A4D4A] mt-0.5">Highest standard</p>
              </div>
            </div>
          </div>

          {/* Right portrait column */}
          <div className="lg:col-span-5 relative">
            <div
              aria-hidden="true"
              className="hidden md:block absolute -top-4 -right-4 w-full h-full border border-[#1B2E26]/25"
            />
            <div className="relative aspect-[4/5] w-full overflow-hidden bg-[#EBE7E0]">
              <div
                aria-hidden="true"
                className="absolute inset-0 bg-gradient-to-br from-[#EBE7E0] via-[#F3F0EA] to-[#DCD7CE]"
              />
              <img
                src="https://customer-assets-gfyr7b9c.emergentagent.net/job_cbt-act-safety/artifacts/itf7wd7w_photo%20profile%20enhanced%20%281%29.webp"
                alt="Dr. Chandril Ghosh — Clinical Psychologist"
                className="relative w-full h-full object-cover object-top mix-blend-multiply"
                loading="eager"
                data-testid="hero-portrait-image"
              />
              <div className="absolute inset-0 ring-1 ring-inset ring-[#1B2E26]/10 pointer-events-none" />
              <div className="hidden md:flex absolute top-5 left-5 items-center gap-3">
                <span className="block w-7 h-px bg-[#1B2E26]/50" />
                <span className="font-serif-display text-[10px] uppercase tracking-[0.28em] text-[#1B2E26]/70">
                  Est. 2025 · Nottingham
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
